/**
 * Form validation utilities
 * Contains reusable validation functions for form fields across the application
 */

/** True if empty, or a US phone with 10 digits (or 11 with country code 1). Matches `formatPhoneString` output and common pasted formats. */
export const isValidUsPhoneDisplay = (val: string | null | undefined): boolean => {
  if (val == null || val.length === 0) return true;
  const digits = val.replace(/\D/g, '');
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
};

/**
 * Validates email format
 * @param email - The email to validate
 * @returns Empty string if valid, error message if invalid
 */
export const validateEmail = (email: string) => {
  if (!email) return '';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Invalid email format';
  return '';
};

/**
 * Validates phone number format (10 digits)
 * @param phone - The phone number to validate
 * @returns Empty string if valid, error message if invalid
 */
export const validatePhone = (phone: string) => {
  if (!phone) return '';
  if (!isValidUsPhoneDisplay(phone)) return 'Phone number must be a valid 10-digit US number';
  return '';
};

/**
 * Validates US state code format (2 uppercase letters)
 * @param state - The state code to validate
 * @returns Empty string if valid, error message if invalid
 */
export const validateState = (state: string) => {
  if (!state) return 'State is required';
  if (!/^[A-Z]{2}$/.test(state.toUpperCase())) return 'State must be 2-letter code';
  return '';
};

/**
 * Validates ZIP code format (5 digits)
 * @param zip - The ZIP code to validate
 * @returns Empty string if valid, error message if invalid
 */
export const validateZip = (zip: string) => {
  if (!/^\d{5}(-\d{4})?$/.test(zip)) {
    return 'ZIP code must be 5 digits or ZIP+4 format';
  }
  return '';
};

/**
 * Validates required field
 * @param value - The value to validate
 * @param fieldName - The name of the field for the error message
 * @returns Empty string if valid, error message if invalid
 */
export const validateRequired = (value: string, fieldName: string) => {
  if (!value?.trim()) return `${fieldName} is required`;
  return '';
};

export const validateRequiredObject = (value: object, fieldName: string) => {
  if (!value) return `${fieldName} is required`;
  else return '';
};

/**
 * Type for form validation errors
 */
export type FormValidationError = {
  message: string;
};
